import React from "react";
import { Box, Typography } from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import Grid2 from "@mui/material/Grid2";
import { Stack } from "@mui/system";

import { Person } from "api/Community/CommunityInterfaces";
import EditableFieldsTable from "components/Records/RecordDisplay/EditableFieldsTable";
import AddressPickerDialog from "components/Flow/Controls/AddressPickerDialog";
import { addressesAPI } from "api/Community/AddressesAPI";
import { RoundIconButton } from "components/Base/Buttons";

import TabPanel from "./TabPanel";

const formatAddress = (a: any) => {
  if (!a) return "";

  const street = a.street_address || a.address || "";
  const city = a.city || "";
  const state = a.state || "";
  const post = a.post_code || "";
  const country = a.country || "";

  return [street, city, state, post, country].filter(Boolean).join(", ");
};

interface Props {
  tabIndex: number;
  index: number;

  data: Person;
  saving: boolean;

  onUpdate: (id: number, field: keyof Person, value: any) => Promise<void>;

  addressPickerOpen: boolean;
  setAddressPickerOpen: (open: boolean) => void;

  pickedAddresses: any[];
  setPickedAddresses: React.Dispatch<React.SetStateAction<any[]>>;

  confirmAddresses: () => Promise<void>;
}

const PeopleProfileTab: React.FC<Props> = ({
  tabIndex,
  index,
  data,
  onUpdate,
  addressPickerOpen,
  setAddressPickerOpen,
  pickedAddresses,
  setPickedAddresses,
  confirmAddresses,
}) => {
  return (
    <TabPanel value={tabIndex} index={index}>
      <Grid2 container>
        {/* Personal Info */}
        <Grid2 size={6}>
          <EditableFieldsTable<Person>
            title="Personal Details"
            record={data}
            onSave={(field, value) => onUpdate(data.id, field, value)}
            fields={[
              {
                field: "gender",
                label: "Gender",
                type: "select",
                placeholder: "Select gender",
                options: [
                  { value: "male", label: "Male" },
                  { value: "female", label: "Female" },
                  { value: "unsure", label: "Unsure" },
                  { value: "other", label: "Other" },
                ],
              },
              {
                field: "date_of_birth",
                label: "Date of Birth",
                type: "date",
              },
              {
                field: "age",
                label: "Age",
                type: "integer",
                min: 0,
                max: 130,
              },
              {
                field: "child",
                label: "Child",
                type: "boolean",
              },
              {
                field: "relationship_status",
                label: "Relationship Status",
                type: "select",
                placeholder: "Select relationship status",
                options: [
                  { value: "single", label: "Single" },
                  { value: "married", label: "Married" },
                  { value: "divorced", label: "Divorced" },
                  { value: "widowed", label: "Widowed" },
                  { value: "separated", label: "Separated" },
                  { value: "other", label: "Other" },
                ],
              },
              {
                field: "preferred_name",
                label: "Preferred Name",
                type: "text",
              },
              {
                field: "languages_spoken",
                label: "Languages Spoken",
                type: "text",
              },
              {
                field: "employment_status",
                label: "Employment Status",
                type: "select",
                placeholder: "Select employment status",
                options: [
                  { value: "employed", label: "Employed" },
                  { value: "unemployed", label: "Unemployed" },
                  { value: "student", label: "Student" },
                  { value: "retired", label: "Retired" },
                  { value: "self_employed", label: "Self-Employed" },
                  { value: "other", label: "Other" },
                ],
              },
            ]}
          />
        </Grid2>

        {/* Contact Info + Address Selection */}
        <Grid2 size={6}>
          <EditableFieldsTable<Person>
            record={data}
            onSave={(field, value) => onUpdate(data.id, field, value)}
            title="Contact Details"
            fields={[
              { field: "phone", label: "Phone" },
              { field: "email", label: "Email" },
            ]}
          />

          <Grid2 container spacing={2}>
            {/* Left: label + button */}
            <Grid2 size={6}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 500,
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  Addresses
                </Typography>

                <RoundIconButton
                  colour="clear"
                  displaySize="inline"
                  onClick={() => setAddressPickerOpen(true)}
                  icon={
                    pickedAddresses.length > 0 ? (
                      <EditIcon fontSize="small" />
                    ) : (
                      <AddIcon fontSize="small" />
                    )
                  }
                />
              </Stack>
            </Grid2>

            {/* Right: list */}
            <Grid2 size={6}>
              {pickedAddresses.length === 0 ||
              pickedAddresses[0] === "" ||
              pickedAddresses[0] === " " ? (
                <Typography variant="body2">No addresses listed</Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {pickedAddresses.map((a: any) => (
                    <React.Fragment key={a.id}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">
                          {formatAddress(a)}
                        </Typography>

                        <RoundIconButton
                          colour="clear"
                          displaySize="inline"
                          icon={<LaunchIcon fontSize="small" />}
                          onClick={() =>
                            window.open(
                              `/community/addresses/${a?.id}/`,
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                        />
                      </Stack>
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Grid2>
          </Grid2>

          {/* Address Picker Dialog */}
          <AddressPickerDialog
            open={addressPickerOpen}
            title="Choose Addresses"
            fetchAddresses={addressesAPI.getAll}
            selected={pickedAddresses}
            onSelectedChange={setPickedAddresses}
            disabledIds={[]}
            confirmLabel={"Save Addresses"}
            onClose={() => {
              const existing = (data as any)?.addresses;
              setPickedAddresses(Array.isArray(existing) ? existing : []);
              setAddressPickerOpen(false);
            }}
            onConfirm={confirmAddresses}
          />
        </Grid2>
      </Grid2>
    </TabPanel>
  );
};

export default PeopleProfileTab;